import * as R from 'ramda'
import * as React from 'react'
import type {RouteComponentProps, RouteProps} from 'react-router-dom'
import {Route} from 'react-router-dom'
import type {OmitStrict} from 'type-zoo'
import type AuthenticatedPlayer from '~/models/app/AuthenticatedPlayer'
import NotFound from '~/pages/NotFound'
import {useSelector} from '~/redux/hooks'
import {ROUTE_TYPE_KEY} from '~/routing/Route/types'

export interface IAdminRouteComponentProps<
  DynamicPathPieces extends {
    [K in keyof DynamicPathPieces]?: string | undefined
  } = void,
>
  extends RouteComponentProps<DynamicPathPieces>, ExtraProps {}

interface PropsFromRouter extends OmitStrict<RouteProps, 'component'> {
  component: React.ComponentType<IAdminRouteComponentProps>
}

interface IProps extends PropsFromRouter {}

const ROUTE_TYPE = 'AdminRoute'

interface ExtraProps {
  [ROUTE_TYPE_KEY]: typeof ROUTE_TYPE
  authenticatedPlayer: AuthenticatedPlayer
}

const Admin = ({component, ...routeProps}: IProps) => {
  const player = useSelector(s => s.authenticatedPlayerState.authenticatedPlayer)

  if (!player?.admin) {
    return <Route component={NotFound} />
  }

  return (
    <Route
      {...routeProps}
      render={routeComponentProps => {
        return React.createElement<IAdminRouteComponentProps<any>>(component, {
          ...routeComponentProps,
          authenticatedPlayer: player,
          [ROUTE_TYPE_KEY]: ROUTE_TYPE,
        })
      }}
    />
  )
}

export default Admin

// since we sometimes need to pass the modified RouteComponentProps down to sub-components
// this helper can filter out all the other props a RouteComponent might have (say, from redux)
// without needing a large {redux1, ..., reduxN, ...routeComponentProps} rest operator
// (we could also try implementing a custom withRouter, but this works for now)
export const isolateAdminRouteComponentProps = <
  DynamicPathPieces extends {[K in keyof DynamicPathPieces]?: string},
>(
  props: IAdminRouteComponentProps<DynamicPathPieces>
): IAdminRouteComponentProps<DynamicPathPieces> => {
  return R.pick(
    [
      ROUTE_TYPE_KEY,
      'authenticatedPlayer',
      'history',
      'match',
      'location',
      'staticContext',
    ],
    props
  )
}
